import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';

function UserProgressView() {
  const { user } = useAuth();
  const [progressData, setProgressData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('last_activity');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchUserProgress();
  }, []);

  const fetchUserProgress = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch('/api/v1/admin/user-progress', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied. Admin privileges required.');
        }
        throw new Error(`Failed to fetch user progress: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setProgressData(data.data || []);
      } else {
        throw new Error(data.error || 'Failed to fetch user progress');
      }
    } catch (err) {
      console.error('Error fetching user progress:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sortData = (field) => {
    const newOrder = sortBy === field && sortOrder === 'desc' ? 'asc' : 'desc';
    setSortBy(field);
    setSortOrder(newOrder);
    
    const sorted = [...progressData].sort((a, b) => {
      let aVal = a[field];
      let bVal = b[field];
      
      if (field === 'last_activity') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (newOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    setProgressData(sorted);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600 bg-green-100';
    if (percentage >= 50) return 'text-yellow-600 bg-yellow-100';
    if (percentage >= 20) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-muted-foreground">Loading user progress...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="text-red-400">⚠️</div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Data</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <button 
              onClick={fetchUserProgress}
              className="mt-2 text-sm text-red-800 underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-accent rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600">
            {progressData.length}
          </div>
          <div className="text-sm text-blue-800">Total Users</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">
            {progressData.reduce((sum, user) => sum + user.total_actions, 0)}
          </div>
          <div className="text-sm text-green-800">Total Actions</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-600">
            {progressData.reduce((sum, user) => sum + user.completed_actions, 0)}
          </div>
          <div className="text-sm text-purple-800">Completed Actions</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-600">
            {Math.round(progressData.reduce((sum, user) => sum + user.time_saved, 0) / 60)} hrs
          </div>
          <div className="text-sm text-orange-800">Time Saved</div>
        </div>
      </div>

      {/* User Progress Table */}
      <div className="bg-card shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 border-b border-border">
          <h3 className="text-lg leading-6 font-medium text-foreground">
            User Progress Overview
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Track user engagement and completion metrics across all domains
          </p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-background">
              <tr>
                <th 
                  onClick={() => sortData('email')}
                  className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  User Email {sortBy === 'email' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th 
                  onClick={() => sortData('domain')}
                  className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Domain {sortBy === 'domain' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th 
                  onClick={() => sortData('total_actions')}
                  className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Actions {sortBy === 'total_actions' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th 
                  onClick={() => sortData('completion_percentage')}
                  className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Progress {sortBy === 'completion_percentage' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th 
                  onClick={() => sortData('time_saved')}
                  className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Time Saved {sortBy === 'time_saved' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th 
                  onClick={() => sortData('last_activity')}
                  className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Last Activity {sortBy === 'last_activity' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-gray-200">
              {progressData.map((user, index) => (
                <tr key={index} className="hover:bg-background">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-foreground">
                      {user.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-foreground">{user.domain}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-foreground">
                      {user.completed_actions} / {user.total_actions}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {user.in_progress_actions} in progress
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-1">
                        <div className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(user.completion_percentage)}`}>
                          {user.completion_percentage}%
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${user.completion_percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-foreground">
                      {Math.round(user.time_saved / 60)} hours
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {user.time_saved} minutes
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {formatDate(user.last_activity)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {progressData.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-4xl mb-4">📊</div>
            <h3 className="text-lg font-medium text-foreground mb-2">No User Progress Data</h3>
            <p className="text-muted-foreground">No users have started working on SEO actions yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserProgressView;